import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './monthlydata.css';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'react-feather';

// Simple loading spinner component
const Spinner = () => (
  <div className="spinner">
    <div className="spinner-inner"></div>
  </div>
);

export default function Monthlydata() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [hasData, setHasData] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage,setItemsPerPage] = useState(10);

  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Format date to YYYY-MM-DD
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-CA');
  };

  // Format date to YYYY-MM (for month column)
  const formatMonth = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-CA', { year: 'numeric', month: 'long' });
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_API_URL}api/announce/getMonthlyAnnounces`, {
        params: {
          page: currentPage,
          limit: itemsPerPage
        }
      });
      
      console.log('API Response:', response.data); // Log the response for debugging
      
      // Handle different response formats
      const responseData = response?.data || {};
      let items = [];
      let total = 0;
      
      // Check if response has the expected format
      if (Array.isArray(responseData.data)) {
        items = responseData.data;
        total = responseData.total || items.length;
      } else if (Array.isArray(responseData)) {
        // If the API returns the array directly
        items = responseData;
        total = items.length;
      } else if (responseData.success && Array.isArray(responseData.data)) {
        // If the API returns {success: true, data: [...]}
        items = responseData.data;
        total = responseData.total || items.length;
      }
      
      setData(items);
      setHasData(items.length > 0);
      setTotalItems(total);
      setTotalPages(Math.ceil(total / itemsPerPage));
      setError(null);
    } catch (err) {
      console.error('Error fetching monthly data:', err);
      setError('Failed to load monthly data. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);


  if (loading) {
    return (
      <div className="loading-container">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-red-600 bg-red-100 rounded">
        {error}
        <button 
          onClick={fetchData}
          className="ml-4 px-4 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Retry
        </button>
      </div>
    );
  }

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const renderPagination = () => (
    <div className="flex items-center justify-between mt-4">
      <div className="text-sm text-gray-600">
        Showing {data.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0} to{' '}
        {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} entries
      </div>
      <div className="flex items-center space-x-1">
        <button
          onClick={() => handlePageChange(1)}
          disabled={currentPage === 1}
          className={`p-2 rounded ${currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`}
        >
          <ChevronsLeft size={18} />
        </button>
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={`p-2 rounded ${currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`}
        >
          <ChevronLeft size={18} />
        </button>
        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
          // Show pages around current page
          let pageNum;
          if (totalPages <= 5) {
            pageNum = i + 1;
          } else if (currentPage <= 3) {
            pageNum = i + 1;
          } else if (currentPage >= totalPages - 2) {
            pageNum = totalPages - 4 + i;
          } else {
            pageNum = currentPage - 2 + i;
          }
          
          return pageNum <= totalPages ? (
            <button
              key={pageNum}
              onClick={() => handlePageChange(pageNum)}
              className={`w-10 h-10 rounded ${
                currentPage === pageNum
                  ? 'bg-blue-500 text-blue'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              {pageNum}
            </button>
          ) : null;
        })}
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={`p-2 rounded ${currentPage === totalPages ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`}
        >
          <ChevronRight size={18} />
        </button>
        <button
          onClick={() => handlePageChange(totalPages)}
          disabled={currentPage === totalPages}
          className={`p-2 rounded ${currentPage === totalPages ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`}
        >
          <ChevronsRight size={18} />
        </button>
      </div>
    </div>
  );

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Monthly Announcements</h1>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-600">Items per page:</span>
          <select
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="border rounded px-2 py-1 text-sm"
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
      </div>
      <div className="table-container">
        <table className="custom-table">
          <thead>
            <tr>
              <th>Client ID</th>
              <th>Client Name</th>
              <th>Month</th>
              <th>Invoice ID</th>
              <th>Invoice Issue Date</th>
              <th>Invoice Amount</th>
              <th>Payment Method</th>
              <th>Payment Status</th>
              <th>Status</th>
              <th>Delegation Status</th>
              <th>Invoice</th>
            </tr>
          </thead>
          <tbody>
            {hasData ? (
              data.map((item) => (
                <tr key={item.id}>
                  <td>{item.email || 'N/A'}</td>
                  <td>{item?.firstname+" "+item?.lastname  || 'N/A'}</td>
                  <td>{formatMonth(item.created_at)}</td>
                  <td>{item.invoice_number || 'N/A'}</td>
                  <td>{formatDate(item.invoice_date)}</td>
                  <td>{
                    item.total !== null && item.total !== undefined 
                      ? `$${Number(item.total).toFixed(2)}` 
                      : 'N/A'
                  }</td>
                  <td>{item.payment_method || 'Stripe'}</td>
                  <td>{item.payment_status==1 ? "Paid" : "Unpaid"}</td>
                  <td>{item.status==3 ? "Completed" : "Pending"}</td>
                  <td>{item.delegate_email ? item.delegate_email : 'Not Delegated'}</td>
                  <td>
                    <button 
                      onClick={() => window.open(`${process.env.REACT_APP_AWS_URL_STATIC_BUSYPLACE}assets/uploads/pdfgenerate/invoice/${item.votre_pdf}`, '_blank')}
                      className="px-3 py-1 bg-primary text-white rounded hover:bg-secondary text-sm"
                      title="Download Invoice"
                    >
                      Download
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="11" className="text-center py-4">No data available</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {renderPagination()}
      {data.length === 0 && !loading && (
        <div className="no-data">No data available</div>
      )}
    </div>
  );
}
